import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, PageBreadcrumb } from '@repo/shared-client/components/blocks'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Rating,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import SFJoinButton from '@web/src/components/primatives/sf-join-button/sf-join-button'
import SFMediaImage from '@web/src/components/primatives/sf-media-image/sf-media-image'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import SFReviewStatus from '@web/src/components/primatives/sf-review-status/sf-review-status'
import SFRichTextViewer from '@web/src/components/primatives/sf-rich-text-viewer/sf-rich-text-viewer'
import MediaAdminEditDialog from '@web/src/features/media/media-admin-edit/media-admin-edit-dialog'
import MediaAdminEditMenuItems from '@web/src/features/media/media-admin-edit/media-admin-edit-menu-items'
import MediaUploaderDialog from '@web/src/features/media/media-uploader-dialog/media-uploader-dialog'
import SimpleMediaImageViewer from '@web/src/features/media/simple-media-image-viewer/simple-media-image-viewer'
import PublicationDelete from '@web/src/features/publication/publication-delete/publication-delete'
import PublicationTransferModal from '@web/src/features/publication/publication-transfer-modal/publication-transfer-modal'
import ReportCreate from '@web/src/features/report/report-create/report-create'
import Reviews, { REVIEWS_QUERY } from '@web/src/features/review/reviews/reviews'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { AttachedMediaInfo, ProfileInfo, ProfileMediaInfo, PublicationInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { ensureAbsoluteWebUrl } from '@web/src/lib/web-url'
import { EllipsisIcon, FilmIcon, LinkIcon } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  DeleteAttachedMediaMutation,
  DeleteAttachedMediaMutationVariables,
  PublicationAdminApproveMutation,
  PublicationAdminApproveMutationVariables,
  PublicationQuery,
  PublicationQueryVariables,
} from 'types/graphql'
import { publicationPageHeadFromLoaderData } from './$publicationIdOrSlug.seo'

const PUBLICATION_QUERY: TypedDocumentNode<PublicationQuery, PublicationQueryVariables> = gql`
  query PublicationQuery($publicationIdOrSlug: String!) {
    publication: publication(publicationIdOrSlug: $publicationIdOrSlug) {
      ...PublicationInfo
      owner {
        ...ProfileInfo
      }
      attachedMedia {
        ...AttachedMediaInfo
      }
    }
  }
  ${PublicationInfo}
  ${AttachedMediaInfo}
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

export const DELETE_IMAGE: TypedDocumentNode<DeleteAttachedMediaMutation, DeleteAttachedMediaMutationVariables> = gql`
  mutation DeleteAttachedMedia($id: String!) {
    deleteAttachedMedia(id: $id) {
      id
    }
  }
`

export const MOVE_MEDIA_UP = gql`
  mutation MoveAttachedMediaUp($id: String!) {
    moveAttachedMediaUp(id: $id) {
      id
    }
  }
`

export const MOVE_MEDIA_DOWN = gql`
  mutation MoveAttachedMediaDown($id: String!) {
    moveAttachedMediaDown(id: $id) {
      id
    }
  }
`

export const MOVE_MEDIA_TO_TOP = gql`
  mutation MoveAttachedMediaToTop($id: String!) {
    moveAttachedMediaToTop(id: $id) {
      id
    }
  }
`

export const MOVE_MEDIA_TO_BOTTOM = gql`
  mutation MoveAttachedMediaToBottom($id: String!) {
    moveAttachedMediaToBottom(id: $id) {
      id
    }
  }
`

export const APPROVE_PUBLICATION: TypedDocumentNode<
  PublicationAdminApproveMutation,
  PublicationAdminApproveMutationVariables
> = gql`
  mutation PublicationAdminApprove($publicationIdOrSlug: String!) {
    publicationAdminApprove(publicationIdOrSlug: $publicationIdOrSlug) {
      ...PublicationInfo
    }
  }
  ${PublicationInfo}
`

export const Route = createFileRoute('/(public)/(main-layout)/publication/$publicationIdOrSlug')({
  component: PublicationPage,

  head: ({ loaderData }) => publicationPageHeadFromLoaderData(loaderData),
  loader: async ({ context, params }) => {
    const [{ queryData: publicationData, queryRef: publicationQueryRef }, { queryData: reviewsData }] =
      await Promise.all([
        queryAndPreload(context, PUBLICATION_QUERY, {
          variables: { publicationIdOrSlug: params.publicationIdOrSlug },
        }),
        queryAndPreload(context, REVIEWS_QUERY, {
          variables: { filter: { publicationIdOrSlug: params.publicationIdOrSlug } },
        }),
      ])

    return {
      queryRef: publicationQueryRef,
      publicationQueryData: publicationData,
      publication: publicationData?.publication,
      reviews: reviewsData?.reviews,
    }
  },
})

type PublicationType = NonNullable<PublicationQuery['publication']>
type PublicationMedia = NonNullable<PublicationType['attachedMedia']> extends Array<infer Media> ? Media : never

const THUMB_CARD =
  'cursor-pointer overflow-hidden rounded-xl border border-border bg-muted transition-colors hover:border-ring hover:bg-muted/80'

const ImageCard = ({
  publication,
  media,
  idx: imageIdx,
  onImageSelected,
  totalMedia,
  onRefetch,
}: {
  publication: PublicationType
  media: PublicationMedia
  idx: number
  totalMedia: number
  onImageSelected: (idx: number) => void
  onRefetch: () => void | Promise<void>
}) => {
  const { t } = useTranslation()
  const [deleteModal, setDeleteModal] = useState(false)
  const [adminEditModal, setAdminEditModal] = useState(false)

  const [deleteImage] = useMutation(DELETE_IMAGE, {
    onCompleted: () => {
      toast.success(t('Image Deleted'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveUp] = useMutation(MOVE_MEDIA_UP, {
    onCompleted: () => {
      toast.success(t('Media moved up'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveDown] = useMutation(MOVE_MEDIA_DOWN, {
    onCompleted: () => {
      toast.success(t('Media moved down'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveToTop] = useMutation(MOVE_MEDIA_TO_TOP, {
    onCompleted: () => {
      toast.success(t('Media moved to top'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveToBottom] = useMutation(MOVE_MEDIA_TO_BOTTOM, {
    onCompleted: () => {
      toast.success(t('Media moved to bottom'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const runMediaAction = (action: string) => {
    if (action === 'delete') {
      setDeleteModal(true)
    } else if (action === 'moveUp') {
      moveUp({ variables: { id: media.id } })
    } else if (action === 'moveDown') {
      moveDown({ variables: { id: media.id } })
    } else if (action === 'moveToTop') {
      moveToTop({ variables: { id: media.id } })
    } else if (action === 'moveToBottom') {
      moveToBottom({ variables: { id: media.id } })
    }
  }

  const handleDeleteImage = () => {
    setDeleteModal(false)
    deleteImage({ variables: { id: media.id } })
  }

  return (
    <div className="relative">
      <div className="relative aspect-[316/480] w-[125px] min-w-[125px]">
        <Card className={cn(THUMB_CARD, 'h-full w-full p-0')} onClick={() => onImageSelected(imageIdx)}>
          <SFMediaImage
            imageProps={{
              alt: t('{{publicationName}} — additional gallery image', { publicationName: publication.name }),
            }}
            media={media}
          />
        </Card>
      </div>
      {publication.canDeleteMedia ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            className="absolute top-[3px] right-[3px]"
            onClick={(e) => e.stopPropagation()}
            render={
              <Button
                aria-label={t('Media options')}
                className="rounded-full border-black"
                size="icon-sm"
                type="button"
              />
            }
          >
            <EllipsisIcon />
          </DropdownMenuTrigger>
          <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
            {imageIdx > 0 ? (
              <DropdownMenuItem onClick={() => runMediaAction('moveToTop')}>{t('Move to Top')}</DropdownMenuItem>
            ) : null}
            {imageIdx > 0 ? (
              <DropdownMenuItem onClick={() => runMediaAction('moveUp')}>{t('Move Up')}</DropdownMenuItem>
            ) : null}
            {imageIdx < totalMedia - 1 ? (
              <DropdownMenuItem onClick={() => runMediaAction('moveDown')}>{t('Move Down')}</DropdownMenuItem>
            ) : null}
            {imageIdx < totalMedia - 1 ? (
              <DropdownMenuItem onClick={() => runMediaAction('moveToBottom')}>{t('Move to Bottom')}</DropdownMenuItem>
            ) : null}

            <MediaAdminEditMenuItems mediaId={media.id} onOpen={() => setAdminEditModal(true)} />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => runMediaAction('delete')} variant="destructive">
              {t('Delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      <ConfirmDialog
        onConfirm={handleDeleteImage}
        onOpenChange={(e) => setDeleteModal(e.open)}
        open={deleteModal}
        text={t('Are you sure you want to delete this image?')}
        title={t('Are you sure?')}
        variant="delete"
      />
      <MediaAdminEditDialog
        isAttached
        mediaId={media.id}
        onMediaChange={onRefetch}
        onOpenChange={(e) => setAdminEditModal(e.open)}
        open={adminEditModal}
      />
    </div>
  )
}

function PublicationPage() {
  const { t } = useTranslation()
  const loaderData = Route.useLoaderData()
  const client = useApolloClient()
  const { data, error } = useReadQuery(loaderData.queryRef as QueryRef<PublicationQuery>)
  const { currentUser, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  const [uploadImageModal, setUploadImageModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [approveModal, setApproveModal] = useState(false)
  const [reportModal, setReportModal] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [transferModal, setTransferModal] = useState(false)
  const [imageIndex, setImageIndex] = useState(0)

  // Create a refetch function that uses Apollo Client directly to avoid router reloads
  // This refetches the query without triggering TanStack Router's loader system
  const handleRefetch = async () => {
    await client.refetchQueries({ include: [PUBLICATION_QUERY] })
  }

  const [approvePublication] = useMutation(APPROVE_PUBLICATION, {
    onCompleted: () => {
      toast.success(t('Publication Approved'))
      handleRefetch()
    },
    onError: (e) => {
      toast.error(e.message)
    },
  })

  if (error) {
    return <ErrorAlert error={error} />
  }

  const resolvedPublicationQuery = data ?? loaderData.publicationQueryData
  if (!resolvedPublicationQuery?.publication) {
    return <ErrorAlert error={new Error(t('Publication not found'))} />
  }

  const publication = resolvedPublicationQuery.publication

  let reviewValue = 0

  if (publication.reviewCount > 0) {
    reviewValue = Math.min(Math.max(0, publication.reviewSum / publication.reviewCount), 5.0)
  }

  const handleApprovePublication = () => {
    setApproveModal(false)
    approvePublication({ variables: { publicationIdOrSlug: publication.slug ?? publication.id } })
  }

  const runPublicationMenuAction = (action: string) => {
    if (action === 'approve') {
      setApproveModal(true)
    } else if (action === 'edit') {
      navigate({
        to: '/publication/$publicationIdOrSlug/edit',
        params: { publicationIdOrSlug: publication.slug ?? publication.id } as never,
      })
    } else if (action === 'delete') {
      setDeleteModal(true)
    } else if (action === 'report') {
      setReportModal(true)
    }
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-2 py-4">
      <PageBreadcrumb
        items={[
          { label: t('Home'), to: '/' },
          { label: t('Discover Cliterature'), to: '/publication/list' },
          { label: publication.name },
        ]}
      />

      <div className="flex w-full flex-col items-stretch gap-3">
        <div className="flex justify-center">
          {publication.canApprove && publication.reviewStatus !== 'ACCEPTED' ? (
            <SFReviewStatus reviewStatus={publication.reviewStatus} size="lg" />
          ) : null}
        </div>

        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-2">
          <h1 className="line-clamp-1 justify-self-center text-center font-bold text-2xl text-foreground md:justify-self-start md:text-start md:text-3xl">
            {publication?.name}
          </h1>
          <div className="flex flex-wrap items-center justify-center gap-2 md:justify-self-end">
            <div className="flex items-center gap-2">
              <Rating size="sm" value={reviewValue} />
              <span className="text-foreground">{reviewValue.toFixed(1)} </span>
            </div>

            {publication.canApprove || publication.canDelete || publication.canEdit || publication.canReport ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={<Button aria-label={t('Publication options')} size="icon" type="button" />}
                >
                  <EllipsisIcon />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {publication.canApprove && publication.reviewStatus !== 'ACCEPTED' ? (
                    <DropdownMenuItem onClick={() => runPublicationMenuAction('approve')}>
                      {t('Approve')}
                    </DropdownMenuItem>
                  ) : null}
                  {publication.canDelete ? (
                    <DropdownMenuItem onClick={() => runPublicationMenuAction('delete')}>
                      {t('Delete')}
                    </DropdownMenuItem>
                  ) : null}
                  {publication.canEdit ? (
                    <DropdownMenuItem onClick={() => runPublicationMenuAction('edit')}>{t('Edit')}</DropdownMenuItem>
                  ) : null}
                  {publication.canReport ? (
                    <DropdownMenuItem onClick={() => runPublicationMenuAction('report')}>
                      {t('Report')}
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </div>

        <div className="flex w-full justify-between">
          <div className="flex items-center gap-2">
            <span className="text-foreground">{t('Author:')} </span>
            <span className="font-semibold text-foreground">{publication.author}</span>
          </div>
        </div>

        {publication.seriesInfo ? (
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-foreground">{t('Series:')} </span>
              <span className="font-semibold text-foreground">{publication.seriesInfo}</span>
            </div>
          </div>
        ) : null}

        <Separator className="w-full" />

        <div className="flex w-full justify-between">
          <h2 className="line-clamp-2 font-medium text-foreground text-lg">{publication?.tagLine}</h2>
        </div>

        <div className="flex w-full flex-wrap justify-start gap-2 overflow-hidden py-1">
          {publication.tags?.map((tag) => {
            return (
              <Badge className="rounded-sm px-2" key={tag.id} variant="default">
                {tag.name}
              </Badge>
            )
          })}
        </div>

        <div className="flex w-full flex-col gap-3">
          {publication?.attachedMedia && publication.attachedMedia.length > 0 ? (
            <div className="flex w-full items-start gap-3">
              {publication.attachedMedia.map((attachedMedia, idx) => {
                if (idx > 1) {
                  return null
                }

                return (
                  <Card
                    className={cn(THUMB_CARD, 'w-full p-0 md:w-1/2', idx > 0 ? 'hidden md:flex' : 'flex')}
                    key={attachedMedia.id}
                    onClick={() => {
                      setImageIndex(idx)
                      setViewModal(true)
                    }}
                  >
                    <div className="relative aspect-[316/480] w-full">
                      <SFMediaImage
                        imageProps={{
                          alt:
                            idx > 0
                              ? t('{{publicationName}} — secondary hero image', {
                                  publicationName: publication.name,
                                })
                              : t('{{publicationName}} — primary hero image', { publicationName: publication.name }),
                        }}
                        media={attachedMedia}
                      />
                    </div>
                  </Card>
                )
              })}
            </div>
          ) : null}

          <div className="flex w-full items-start justify-start gap-2 overflow-x-auto overflow-y-hidden">
            {publication.canUploadMedia ? (
              <div className="relative aspect-[316/480] w-[125px] min-w-[125px]">
                <Card
                  className={cn(THUMB_CARD, 'flex h-full w-full flex-col items-center justify-center p-0')}
                  onClick={() => setUploadImageModal(true)}
                >
                  <FilmIcon className="size-8 text-muted-foreground" />
                  <p className="text-center font-medium text-base text-foreground">
                    {t('Upload')}
                    <br />
                    {t('Media')}
                  </p>
                </Card>
              </div>
            ) : null}

            {publication.attachedMedia?.map((attachedMedia, idx) => {
              return (
                <ImageCard
                  idx={idx}
                  key={attachedMedia.id}
                  media={attachedMedia}
                  onImageSelected={(index) => {
                    setImageIndex(index)
                    setViewModal(true)
                  }}
                  onRefetch={handleRefetch}
                  publication={publication}
                  totalMedia={publication.attachedMedia.length}
                />
              )
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[3fr_1fr]">
          <SFProfileBanner
            lowerTitle={
              <span className="line-clamp-1 font-normal text-foreground text-sm">{t('Page Maintainer')}</span>
            }
            profile={publication.owner}
            size="lg"
          />
          {publication.canTransfer ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    className="w-full md:w-auto"
                    onClick={() => setTransferModal(true)}
                    type="button"
                    variant="default"
                  />
                }
              >
                {t('Transfer Maintainer')}
              </TooltipTrigger>
              <TooltipContent>{t('Transfer page ownership to another profile.')}</TooltipContent>
            </Tooltip>
          ) : null}
        </div>

        <Separator className="w-full" />

        <div className="flex flex-col items-start gap-2">
          {publication.webLinks?.map((link, idx) => {
            const href = ensureAbsoluteWebUrl(link)
            return (
              <div className="flex w-full flex-col items-start" key={idx}>
                <Link
                  className="inline-flex min-w-0 max-w-full items-center gap-2 text-primary"
                  rel="noopener noreferrer"
                  target="_blank"
                  to={href}
                >
                  <LinkIcon className="size-4 shrink-0" />
                  <span className="line-clamp-1">{link}</span>
                </Link>
              </div>
            )
          })}
        </div>

        <div className="grid grid-cols-1 gap-3">
          <Card>
            <CardHeader>
              <CardTitle>{t('About')}</CardTitle>
            </CardHeader>
            <CardContent>
              <SFRichTextViewer
                className="w-full border-none p-0"
                content={publication.description}
                editorStyles="overflow: hidden"
              />
            </CardContent>
          </Card>
        </div>

        <ConfirmDialog
          confirmText={t('Approve')}
          onConfirm={handleApprovePublication}
          onOpenChange={(e) => setApproveModal(e.open)}
          open={approveModal}
          text={t('Are you sure you want to approve this publication?')}
          title={t('Are you sure?')}
          variant="basic"
        />

        <PublicationTransferModal
          onComplete={handleRefetch}
          onOpenChange={(e) => setTransferModal(e.open)}
          open={transferModal}
          publicationId={publication.id}
        />

        <PublicationDelete onOpenChange={(e) => setDeleteModal(e.open)} open={deleteModal} publication={publication} />

        <ReportCreate
          onOpenChange={(e) => setReportModal(e.open)}
          open={reportModal}
          targetId={publication?.id}
          targetKind="PUBLICATION"
        />

        <MediaUploaderDialog
          onOpenChange={(e) => setUploadImageModal(e.open)}
          onUploadComplete={handleRefetch}
          open={uploadImageModal}
          publicationId={publication?.id}
        />

        {publication.attachedMedia ? (
          <SimpleMediaImageViewer
            attachedMedia={true}
            imageIndex={imageIndex}
            mediaArray={publication.attachedMedia}
            onMediaChange={handleRefetch}
            onOpenChange={(e) => setViewModal(e.open)}
            open={viewModal}
            publicationId={publication.id}
          />
        ) : null}

        {isAuthenticated ? null : (
          <div className="flex justify-center py-6">
            <SFJoinButton />
          </div>
        )}

        <Reviews
          canReview={publication.canReview}
          onReviewChange={handleRefetch}
          profileId={currentUser?.profile?.id}
          publicationIdOrSlug={publication.slug ?? publication.id}
          publicationOwnerId={publication.ownerId ?? undefined}
          scrollableId={null}
        />
      </div>
    </div>
  )
}
